import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, TextField, Button, IconButton, Chip,
    Autocomplete, Select, MenuItem, FormControl, Collapse,
    Tooltip, CircularProgress, Alert, Accordion, AccordionSummary,
    AccordionDetails, Tabs, Tab
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Cancel as CancelIcon,
    ExpandMore as ExpandMoreIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Code as CodeIcon,
    GridView as GridViewIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
    Casino as CasinoIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getAllLimitedCards, getAllArchetypes, getDecksWithArchetypes, getDecksByArchetype, getDeck, normalizeTrackerSetId } from '../../services/tracker-limited-data';
import { InkColorIcons } from './InkColorIcon';

// ─── Constants ────────────────────────────────────────────────────────────────

const FAVORABILITY_OPTIONS = [
    { value: 'very_favored',   label: 'Bardzo faworyzowany' },
    { value: 'favored',        label: 'Faworyzowany' },
    { value: 'even',           label: 'Wyrównany' },
    { value: 'unfavored',      label: 'Niefaworyzowany' },
    { value: 'very_unfavored', label: 'Bardzo niefaworyzowany' },
];

const DIFFICULTY_OPTIONS = ['low', 'medium', 'high'];
const DIFFICULTY_LABELS  = { low: 'Łatwy', medium: 'Średni', high: 'Trudny' };
const PLAYSTYLE_OPTIONS  = ['aggro', 'midrange', 'control', 'combo', 'tempo'];

const CARD_W = { xs: 46, sm: 54 };
const CARD_H = { xs: 65, sm: 76 };

// ─── Blank draft ──────────────────────────────────────────────────────────────

const hasMulliganContent = (side) =>
    (side?.priorities?.length > 0) || (side?.example_hands?.length > 0);

const inferUnified = (mulligan) =>
    !hasMulliganContent(mulligan?.otd);

function blankDraft(existing = null) {
    const base = {
        schema_version: 1,
        meta: { deck_list_id: null, set_scope: '', author_note: '' },
        tldr: { summary: '', playstyle_tags: [], difficulty: 'medium', matchup_snapshot: [], key_cards: [] },
        card_breakdown: [
            { group: 'Core', cards: [] },
            { group: 'Flex', cards: [] },
            { group: 'Tech', cards: [] },
        ],
        gameplan: { text: '', play_lines: [] },
        mulligan: {
            general_notes: '',
            unified: true,
            otp: { priorities: [], ideal_line: [], example_hands: [] },
            otd: { priorities: [], ideal_line: [], example_hands: [] },
        },
        matchups: [],
        alternative_builds: [],
    };
    if (!existing || existing.schema_version !== 1) return base;
    return {
        ...base,
        ...existing,
        meta: { ...base.meta, ...(existing.meta || {}) },
        tldr: { ...base.tldr, ...(existing.tldr || {}), key_cards: existing.tldr?.key_cards || [] },
        card_breakdown: existing.card_breakdown || base.card_breakdown,
        gameplan: { ...base.gameplan, ...(existing.gameplan || {}) },
        mulligan: {
            ...base.mulligan,
            ...(existing.mulligan || {}),
            unified: existing.mulligan?.unified ?? inferUnified(existing.mulligan),
            otp: { ...base.mulligan.otp, ...(existing.mulligan?.otp || {}) },
            otd: { ...base.mulligan.otd, ...(existing.mulligan?.otd || {}) },
        },
        matchups: existing.matchups || [],
        alternative_builds: existing.alternative_builds || [],
    };
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

const isSpecialRarity = (rarity) => {
    const r = (rarity || '').toLowerCase();
    return r === 'enchanted' || r === 'special' || r === 'epic' || r === 'promo';
};

const isReprintCard = (card) => {
    const identifier = card.card_identifier || card.collector_number || '';
    if (!identifier) return false;
    const [first, second] = String(identifier).split('/');
    const firstNum = parseInt(first, 10);
    const secondNum = parseInt(second, 10);
    return !isNaN(firstNum) && !isNaN(secondNum) && firstNum > secondNum;
};

const getBestVariantImages = (card) => {
    const variants = Array.isArray(card.variants) ? card.variants : [];
    const specials = ['enchanted', 'special', 'epic', 'promo', 'foil', 'alt art'];
    const isSpecialVariant = (v) => specials.some((s) => [v?.rarity, v?.type, v?.finish, v?.variant, v?.name].some((f) => (f || '').toLowerCase().includes(s)));
    const preferred = variants.find((v) => !isSpecialVariant(v)) || variants[0] || {};
    return {
        thumbnail: preferred.thumbnail_image_url || preferred.thumb_image_url || preferred.card_image_url || preferred.detail_image_url || card.image_urls?.[0]?.url || null,
        detail: preferred.detail_image_url || preferred.card_image_url || card.image_urls?.[0]?.url || null,
    };
};

function useCardOptions() {
    const [options, setOptions] = useState([]);
    const [lookup, setLookup] = useState({});
    const [ready, setReady] = useState(false);

    useEffect(() => {
        getAllLimitedCards()
            .then((cards) => {
                const seen = new Set();
                const opts = [];
                const map = {};
                (cards || [])
                    .filter((card) => !isSpecialRarity(card.rarity) && !isReprintCard(card))
                    .forEach((card) => {
                        const name = card.name || '';
                        const subtitle = card.subtitle || '';
                        const dedupeKey = `${name.toLowerCase().trim()}::${subtitle.toLowerCase().trim()}`;
                        if (seen.has(dedupeKey)) return;
                        seen.add(dedupeKey);
                        const fullName = subtitle ? `${name} - ${subtitle}` : name;
                        const { thumbnail, detail } = getBestVariantImages(card);
                        const entry = { label: fullName, name, thumbnail, detail, magic_ink_colors: card.magic_ink_colors || [] };
                        opts.push(entry);
                        [fullName, name].forEach((n) => {
                            const key = n.toLowerCase().trim();
                            if (key && !map[key]) map[key] = entry;
                        });
                    });
                setOptions(opts);
                setLookup(map);
            })
            .catch(() => {})
            .finally(() => setReady(true));
    }, []);

    const get = useCallback((name) => {
        if (!name) return null;
        return lookup[name.toLowerCase().trim()] || null;
    }, [lookup]);

    return { options, get, ready };
}

function useArchetypeOptions(filters) {
    const [options, setOptions] = useState([]);
    useEffect(() => {
        getAllArchetypes(filters || {})
            .then((data) => {
                const arr = Array.isArray(data) ? data : (data?.archetypes || []);
                const format = filters?.format || null;
                const setId = normalizeTrackerSetId(filters?.setId) || null;
                setOptions(
                    arr
                        .filter((a) => {
                            const aFormat = a.formatScope || null;
                            const aSet = normalizeTrackerSetId(a.setScope) || null;
                            if (format && aFormat && aFormat !== format) return false;
                            if (setId && aSet && aSet !== setId) return false;
                            return true;
                        })
                        .map((a) => ({
                            id: a.id || a.archetype_id || '',
                            name: a.name || a.display_name || a.id || '',
                            ink_colors: a.ink_colors || [],
                        }))
                );
            })
            .catch(() => {});
    }, [filters?.format, filters?.setId]); // eslint-disable-line react-hooks/exhaustive-deps
    return options;
}

function useInternalDecks(filters, archetypeId) {
    const [decks, setDecks] = useState([]);
    useEffect(() => {
        const fetch = archetypeId
            ? getDecksByArchetype(archetypeId, null, filters || {})
            : getDecksWithArchetypes(null, filters || {});
        fetch
            .then((data) => {
                const arr = Array.isArray(data) ? data : [];
                setDecks(arr.map((d) => ({
                    id: d.id || '',
                    name: d.name || '',
                    ink_colors: d.archetypeInfo?.ink_colors || d.ink_colors || [],
                })));
            })
            .catch(() => {});
    }, [archetypeId, filters?.format, filters?.setId]); // eslint-disable-line react-hooks/exhaustive-deps
    return decks;
}

function useDeckCards(deckId) {
    const [cardKeys, setCardKeys] = useState(new Set());
    useEffect(() => {
        if (!deckId) { setCardKeys(new Set()); return; }
        getDeck(deckId)
            .then((deck) => {
                const cards = deck?.deckVisual?.cards || [];
                const keys = new Set();
                cards.forEach((card) => {
                    const name = card.name || '';
                    const subtitle = card.subtitle || '';
                    const fullName = subtitle ? `${name} - ${subtitle}` : name;
                    keys.add(fullName.toLowerCase().trim());
                    if (name) keys.add(name.toLowerCase().trim());
                });
                setCardKeys(keys);
            })
            .catch(() => setCardKeys(new Set()));
    }, [deckId]);
    return cardKeys;
}

function useDeckPool(deckId) {
    const [pool, setPool] = useState([]);
    useEffect(() => {
        if (!deckId) { setPool([]); return; }
        getDeck(deckId)
            .then((deck) => {
                const cards = deck?.deckVisual?.cards || [];
                const expanded = [];
                cards.forEach((card) => {
                    const name = card.name || '';
                    const subtitle = card.subtitle || '';
                    const fullName = subtitle ? `${name} - ${subtitle}` : name;
                    const qty = card.quantity || 1;
                    for (let i = 0; i < qty; i++) expanded.push(fullName);
                });
                setPool(expanded);
            })
            .catch(() => setPool([]));
    }, [deckId]);
    return pool;
}

function drawRandomHand(pool, count = 7) {
    if (!pool.length) return [];
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(count, shuffled.length));
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255,255,255,0.9)',
        fontSize: '0.875rem',
        bgcolor: 'rgba(0,0,0,0.2)',
        '& fieldset': { borderColor: 'rgba(76,29,149,0.35)' },
        '&:hover fieldset': { borderColor: 'rgba(124,58,237,0.55)' },
        '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(196,181,253,0.5)', fontSize: '0.82rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#c4b5fd' },
};

const selectSx = {
    color: 'rgba(255,255,255,0.9)',
    fontSize: '0.875rem',
    bgcolor: 'rgba(0,0,0,0.2)',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76,29,149,0.35)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(124,58,237,0.55)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' },
    '& .MuiSelect-icon': { color: 'rgba(196,181,253,0.5)' },
};

const addBtnSx = {
    color: 'rgba(196,181,253,0.7)', borderColor: 'rgba(76,29,149,0.35)',
    textTransform: 'none', fontSize: '0.78rem', py: 0.4, px: 1.25,
    '&:hover': { borderColor: 'rgba(124,58,237,0.6)', bgcolor: 'rgba(124,58,237,0.07)' },
};

const sectionSx = {
    bgcolor: 'rgba(17,24,39,0.65)',
    border: '1px solid rgba(76,29,149,0.2)',
    borderRadius: '10px',
    p: { xs: 1.5, sm: 2 },
    mb: 2,
};

function Label({ children }) {
    return (
        <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
            {children}
        </Typography>
    );
}

// ─── Card autocomplete ────────────────────────────────────────────────────────

function CardAutocomplete({ options, onSelect, placeholder = 'Szukaj karty…', size = 'small', sx = {} }) {
    const [val, setVal] = useState(null);
    const [inputVal, setInputVal] = useState('');

    return (
        <Autocomplete
            value={val}
            inputValue={inputVal}
            onInputChange={(_, v) => setInputVal(v)}
            onChange={(_, opt) => {
                if (opt) { onSelect(opt.label); setVal(null); setInputVal(''); }
            }}
            options={options}
            filterOptions={(opts, { inputValue }) => {
                if (!inputValue.trim()) return [];
                const q = inputValue.toLowerCase();
                return opts.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 50);
            }}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
            isOptionEqualToValue={(o, v) => o.label === v?.label}
            noOptionsText={inputVal.trim() ? 'Brak wyników' : 'Wpisz nazwę karty…'}
            renderOption={(props, opt) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '6px !important' }}>
                    {opt.thumbnail ? (
                        <Box component="img" src={opt.thumbnail} alt={opt.label}
                            sx={{ height: 32, width: 23, borderRadius: '3px', objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                        <Box sx={{ width: 23, height: 32, bgcolor: 'rgba(124,58,237,0.15)', borderRadius: '3px', flexShrink: 0 }} />
                    )}
                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.88)' }}>{opt.label}</Typography>
                </Box>
            )}
            renderInput={(params) => (
                <TextField {...params} size={size} placeholder={placeholder} sx={{ ...inputSx, ...sx }} />
            )}
            componentsProps={{
                popper: { sx: { '& .MuiPaper-root': { bgcolor: '#1a1033', border: '1px solid rgba(76,29,149,0.4)', borderRadius: '8px' } } },
                paper: { sx: { bgcolor: '#1a1033' } },
            }}
            sx={{ minWidth: 220 }}
        />
    );
}

// ─── Mini card thumbnail with optional count badge and remove ─────────────────

function CardThumb({ name, cardData, count, dimmed, showX, onRemove, onClick, showRemoveBtn }) {
    return (
        <Tooltip
            arrow
            placement="top"
            title={cardData?.detail ? (
                <Box sx={{ p: 0.5 }}>
                    <Box component="img" src={cardData.detail} alt={name}
                        sx={{ width: 160, borderRadius: '6px', display: 'block' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(196,181,253,0.8)', mt: 0.5, textAlign: 'center' }}>{name}</Typography>
                </Box>
            ) : name}
            componentsProps={{ tooltip: { sx: { bgcolor: 'rgba(17,24,39,0.97)', border: '1px solid rgba(76,29,149,0.4)', p: cardData?.detail ? 0.75 : undefined } } }}
        >
            <Box sx={{ position: 'relative', flexShrink: 0, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
                {cardData?.thumbnail ? (
                    <Box
                        component="img"
                        src={cardData.thumbnail}
                        alt={name}
                        sx={{
                            width: CARD_W, height: CARD_H,
                            borderRadius: '6px', objectFit: 'cover', display: 'block',
                            border: `1.5px solid ${dimmed ? 'rgba(239,68,68,0.4)' : 'rgba(124,58,237,0.35)'}`,
                            filter: dimmed ? 'brightness(0.3) saturate(0.5)' : 'none',
                        }}
                    />
                ) : (
                    <Box sx={{
                        width: CARD_W, height: CARD_H, borderRadius: '6px', flexShrink: 0,
                        bgcolor: 'rgba(124,58,237,0.1)', border: '1.5px solid rgba(124,58,237,0.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5,
                        filter: dimmed ? 'brightness(0.3)' : 'none',
                    }}>
                        <Typography sx={{ fontSize: '0.52rem', color: 'rgba(255,255,255,0.7)', textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-word' }}>
                            {name}
                        </Typography>
                    </Box>
                )}
                {showX && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}>
                        <CancelIcon sx={{ color: '#ef4444', fontSize: { xs: 22, sm: 26 }, filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.9))' }} />
                    </Box>
                )}
                {count != null && (
                    <Box sx={{
                        position: 'absolute', top: -6, right: -6, zIndex: 2,
                        bgcolor: '#7c3aed', color: 'white', borderRadius: '50%',
                        width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.6rem', fontWeight: 700, border: '1.5px solid rgba(17,24,39,0.95)',
                    }}>
                        {count}
                    </Box>
                )}
                {showRemoveBtn && (
                    <Box
                        onClick={(e) => { e.stopPropagation(); onRemove?.(); }}
                        sx={{
                            position: 'absolute', top: -6, left: -6, zIndex: 3,
                            bgcolor: 'rgba(239,68,68,0.85)', color: 'white', borderRadius: '50%',
                            width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', border: '1px solid rgba(17,24,39,0.9)',
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 10 }} />
                    </Box>
                )}
            </Box>
        </Tooltip>
    );
}

// ─── Mulligan hand builder ─────────────────────────────────────────────────────

function MulliganHandBuilder({ cardNames = [], mulliganIndices = [], onChange, get, cardOptions, deckPool = [] }) {
    const [adding, setAdding] = useState(false);

    const toggleDiscard = (i) => {
        const next = mulliganIndices.includes(i)
            ? mulliganIndices.filter((x) => x !== i)
            : [...mulliganIndices, i];
        onChange({ card_names: cardNames, mulligan_indices: next });
    };

    const removeCard = (i) => {
        const nextNames = cardNames.filter((_, j) => j !== i);
        const nextIdx = mulliganIndices.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x));
        onChange({ card_names: nextNames, mulligan_indices: nextIdx });
    };

    const addCard = (name) => {
        onChange({ card_names: [...cardNames, name], mulligan_indices: mulliganIndices });
        setAdding(false);
    };

    const redraw = () => {
        const hand = drawRandomHand(deckPool);
        onChange({ card_names: hand, mulligan_indices: [] });
        setAdding(false);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.75 }, flexWrap: 'wrap', alignItems: 'flex-end', mb: 1 }}>
                {cardNames.map((name, i) => {
                    const discard = mulliganIndices.includes(i);
                    return (
                        <CardThumb
                            key={i}
                            name={name}
                            cardData={get(name)}
                            dimmed={discard}
                            showX={discard}
                            showRemoveBtn
                            onRemove={() => removeCard(i)}
                            onClick={() => toggleDiscard(i)}
                        />
                    );
                })}
                <Box
                    onClick={() => setAdding((v) => !v)}
                    sx={{
                        width: CARD_W, height: CARD_H,
                        borderRadius: '6px', border: '1.5px dashed rgba(124,58,237,0.4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', flexShrink: 0, color: 'rgba(196,181,253,0.5)',
                        bgcolor: adding ? 'rgba(124,58,237,0.08)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(124,58,237,0.1)', borderColor: 'rgba(124,58,237,0.6)' },
                        transition: 'all 0.15s',
                    }}
                >
                    <AddIcon sx={{ fontSize: 22 }} />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {cardNames.length > 0 && (
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(196,181,253,0.4)', flex: 1 }}>
                        Kliknij kartę — pomarańcz = wyrzuć z ręki · × = usuń z przykładu
                    </Typography>
                )}
                {deckPool.length > 0 && cardNames.length > 0 && (
                    <Box onClick={redraw} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.3,
                        borderRadius: '5px', cursor: 'pointer',
                        border: '1px solid rgba(76,29,149,0.3)', color: 'rgba(196,181,253,0.45)',
                        '&:hover': { bgcolor: 'rgba(124,58,237,0.1)', color: '#c4b5fd', borderColor: 'rgba(124,58,237,0.5)' },
                        transition: 'all 0.12s',
                    }}>
                        <RefreshIcon sx={{ fontSize: 13 }} />
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 500 }}>Przetasuj</Typography>
                    </Box>
                )}
            </Box>
            <Collapse in={adding}>
                <CardAutocomplete options={cardOptions} onSelect={addCard} placeholder="Dodaj kartę do ręki…" />
            </Collapse>
        </Box>
    );
}

// ─── StringListEditor ─────────────────────────────────────────────────────────

function StringListEditor({ items = [], onChange, placeholder, addLabel = 'Dodaj' }) {
    return (
        <Box>
            {items.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.75, alignItems: 'flex-start' }}>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        value={item}
                        placeholder={placeholder}
                        onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
                        sx={inputSx}
                    />
                    <IconButton size="small" onClick={() => onChange(items.filter((_, j) => j !== i))}
                        sx={{ color: 'rgba(239,68,68,0.6)', mt: 0.5, '&:hover': { color: '#ef4444' } }}>
                        <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            ))}
            <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => onChange([...items, ''])} sx={addBtnSx}>
                {addLabel}
            </Button>
        </Box>
    );
}

// ─── TurnListEditor ───────────────────────────────────────────────────────────

function TurnListEditor({ turns = [], onChange, get, cardOptions }) {
    const update = (i, field, val) =>
        onChange(turns.map((t, j) => (j === i ? { ...t, [field]: val } : t)));

    const addCard = (i, name) => {
        const cur = turns[i]?.card_names || [];
        update(i, 'card_names', [...cur, name]);
    };

    const removeCard = (i, ci) => {
        const cur = turns[i]?.card_names || [];
        update(i, 'card_names', cur.filter((_, j) => j !== ci));
    };

    return (
        <Box>
            {turns.map((row, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                    <Box sx={{
                        width: 28, height: 36, borderRadius: '6px', flexShrink: 0,
                        bgcolor: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#7c3aed', fontWeight: 700, fontSize: '0.75rem',
                    }}>T{row.turn}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            size="small"
                            fullWidth
                            value={row.action || ''}
                            placeholder="Akcja…"
                            onChange={(e) => update(i, 'action', e.target.value)}
                            sx={{ ...inputSx, mb: 0.5 }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {(row.card_names || []).map((cn, ci) => {
                                const cd = get(cn);
                                return (
                                    <Box key={ci} sx={{ display: 'flex', alignItems: 'center', gap: 0.25,
                                        bgcolor: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)',
                                        borderRadius: '5px', px: 0.5, py: 0.15 }}>
                                        {cd?.thumbnail && (
                                            <Box component="img" src={cd.thumbnail} alt={cn}
                                                sx={{ height: 20, width: 14, borderRadius: '2px', objectFit: 'cover' }} />
                                        )}
                                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.85)' }}>{cn}</Typography>
                                        <IconButton size="small" onClick={() => removeCard(i, ci)}
                                            sx={{ p: 0, color: 'rgba(239,68,68,0.6)', ml: 0.25 }}>
                                            <CloseIcon sx={{ fontSize: 12 }} />
                                        </IconButton>
                                    </Box>
                                );
                            })}
                            <CardAutocomplete
                                options={cardOptions}
                                onSelect={(name) => addCard(i, name)}
                                placeholder="+ karta"
                                sx={{ '& .MuiOutlinedInput-root': { py: '2px !important', fontSize: '0.75rem' } }}
                            />
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={() => onChange(turns.filter((_, j) => j !== i))}
                        sx={{ color: 'rgba(239,68,68,0.5)', mt: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            ))}
            <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => onChange([...turns, { turn: (turns.length > 0 ? Math.max(...turns.map(t => t.turn)) + 1 : 1), action: '', card_names: [] }])}
                sx={addBtnSx}>
                Dodaj turę
            </Button>
        </Box>
    );
}

// ─── Meta Editor (deck picker) ────────────────────────────────────────────────

function MetaDeckEditor({ deckListId, onChange, deckOptions }) {
    const [inputVal, setInputVal] = useState('');
    const currentDeck = deckOptions.find(d => d.id === deckListId) || null;

    return (
        <Box sx={sectionSx}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c4b5fd', mb: 1.5 }}>Powiązana talia</Typography>
            <Autocomplete
                value={currentDeck}
                inputValue={inputVal}
                onInputChange={(_, v, reason) => { if (reason !== 'reset') setInputVal(v); }}
                onChange={(_, opt) => { onChange(opt?.id || null); setInputVal(''); }}
                options={deckOptions}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                noOptionsText={inputVal.trim() ? 'Brak wyników' : 'Wpisz nazwę talii…'}
                filterOptions={(opts, { inputValue }) => {
                    if (!inputValue.trim()) return opts.slice(0, 50);
                    const q = inputValue.toLowerCase();
                    return opts.filter(o => o.name.toLowerCase().includes(q)).slice(0, 50);
                }}
                renderOption={(props, opt) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '6px !important' }}>
                        {opt.ink_colors?.length > 0 && (
                            <InkColorIcons colors={opt.ink_colors} size={14} gap={0.2} tooltip={false} sx={{ flexShrink: 0 }} />
                        )}
                        <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.88)' }}>{opt.name}</Typography>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Wybierz talię…" sx={inputSx} />
                )}
                componentsProps={{
                    popper: { sx: { '& .MuiPaper-root': { bgcolor: '#1a1033', border: '1px solid rgba(76,29,149,0.4)', borderRadius: '8px' } } },
                    paper: { sx: { bgcolor: '#1a1033' } },
                }}
            />
            {currentDeck && (
                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {currentDeck.ink_colors?.length > 0 && (
                        <InkColorIcons colors={currentDeck.ink_colors} size={16} gap={0.25} tooltip={false} />
                    )}
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(196,181,253,0.55)' }}>{currentDeck.name}</Typography>
                </Box>
            )}
        </Box>
    );
}

// ─── TL;DR Editor ─────────────────────────────────────────────────────────────

function TldrEditor({ value, onChange, get, cardOptions }) {
    const upd = (k, v) => onChange({ ...value, [k]: v });

    const addKeyCard = (name) => {
        const cur = value.key_cards || [];
        if (!cur.includes(name)) upd('key_cards', [...cur, name]);
    };
    const removeKeyCard = (i) => upd('key_cards', (value.key_cards || []).filter((_, j) => j !== i));

    const toggleTag = (tag) => {
        const tags = value.playstyle_tags || [];
        upd('playstyle_tags', tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag]);
    };

    return (
        <Box sx={sectionSx}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c4b5fd', mb: 1.5 }}>TL;DR</Typography>

            <Label>Opis (jedno zdanie)</Label>
            <TextField size="small" fullWidth multiline minRows={2} value={value.summary || ''} onChange={(e) => upd('summary', e.target.value)} placeholder="Krótkie streszczenie talii…" sx={{ ...inputSx, mb: 2 }} />

            <Label>Styl gry</Label>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                {PLAYSTYLE_OPTIONS.map((tag) => {
                    const active = (value.playstyle_tags || []).includes(tag);
                    return (
                        <Chip key={tag} label={tag} size="small" onClick={() => toggleTag(tag)} sx={{
                            bgcolor: active ? 'rgba(124,58,237,0.3)' : 'rgba(255,255,255,0.05)',
                            color: active ? '#c4b5fd' : 'rgba(255,255,255,0.55)',
                            border: `1px solid ${active ? 'rgba(124,58,237,0.55)' : 'rgba(255,255,255,0.12)'}`,
                            fontWeight: active ? 700 : 400, cursor: 'pointer', textTransform: 'capitalize',
                            '&:hover': { bgcolor: 'rgba(124,58,237,0.2)' },
                        }} />
                    );
                })}
            </Box>

            <Label>Trudność</Label>
            <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
                {DIFFICULTY_OPTIONS.map((d) => {
                    const active = value.difficulty === d;
                    return (
                        <Box key={d} onClick={() => upd('difficulty', d)} sx={{
                            px: 1.5, py: 0.4, borderRadius: '7px', cursor: 'pointer',
                            bgcolor: active ? 'rgba(124,58,237,0.28)' : 'rgba(0,0,0,0.2)',
                            border: `1px solid ${active ? 'rgba(124,58,237,0.5)' : 'rgba(76,29,149,0.2)'}`,
                            transition: 'all 0.15s',
                        }}>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: active ? 700 : 400, color: active ? '#c4b5fd' : 'rgba(196,181,253,0.5)' }}>
                                {DIFFICULTY_LABELS[d]}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            <Label>Kluczowe karty (TL;DR — max 6)</Label>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                {(value.key_cards || []).map((cn, i) => {
                    const cd = get ? get(cn) : null;
                    return (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, bgcolor: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.35)', borderRadius: '6px', px: 0.75, py: 0.25 }}>
                            {cd?.thumbnail && (
                                <Box component="img" src={cd.thumbnail} alt={cn} sx={{ height: 24, width: 17, borderRadius: '3px', objectFit: 'cover' }} />
                            )}
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)' }}>{cn}</Typography>
                            <IconButton size="small" onClick={() => removeKeyCard(i)} sx={{ p: 0, color: 'rgba(239,68,68,0.55)', ml: 0.25, '&:hover': { color: '#ef4444' } }}>
                                <DeleteIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                        </Box>
                    );
                })}
            </Box>
            {(value.key_cards || []).length < 6 && cardOptions && (
                <CardAutocomplete options={cardOptions} onSelect={addKeyCard} placeholder="Dodaj kluczową kartę…" />
            )}
        </Box>
    );
}

// ─── Card Breakdown Editor ────────────────────────────────────────────────────

function CardBreakdownEditor({ value = [], onChange, get, cardOptions }) {
    const [tab, setTab] = useState(0);
    const [expanded, setExpanded] = useState(null);

    const updateGroup = (gi, cards) =>
        onChange(value.map((g, j) => (j === gi ? { ...g, cards } : g)));

    const addCard = (gi, name) => {
        const grp = value[gi];
        if (!grp) return;
        const already = grp.cards.find((c) => c.card_name === name);
        if (already) return;
        updateGroup(gi, [...grp.cards, { card_name: name, count: 4, note: '' }]);
        setExpanded(grp.cards.length);
    };

    const updateCard = (gi, ci, field, val) =>
        updateGroup(gi, value[gi].cards.map((c, j) => (j === ci ? { ...c, [field]: val } : c)));

    const removeCard = (gi, ci) => {
        updateGroup(gi, value[gi].cards.filter((_, j) => j !== ci));
        setExpanded(null);
    };

    const addGroup = () => {
        onChange([...value, { group: 'Nowa grupa', cards: [] }]);
        setTab(value.length);
    };

    const removeGroup = (gi) => {
        onChange(value.filter((_, j) => j !== gi));
        setTab(Math.max(0, tab - 1));
    };

    const renameGroup = (gi, name) =>
        onChange(value.map((g, j) => (j === gi ? { ...g, group: name } : g)));

    const activeGroup = value[tab];

    return (
        <Box sx={sectionSx}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c4b5fd', mb: 1.5 }}>Opis kart</Typography>

            <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {value.map((g, i) => (
                    <Box key={i} onClick={() => { setTab(i); setExpanded(null); }} sx={{
                        px: 1.25, py: 0.4, borderRadius: '7px', cursor: 'pointer',
                        bgcolor: tab === i ? 'rgba(124,58,237,0.28)' : 'rgba(0,0,0,0.2)',
                        border: `1px solid ${tab === i ? 'rgba(124,58,237,0.5)' : 'rgba(76,29,149,0.2)'}`,
                        display: 'flex', alignItems: 'center', gap: 0.5,
                    }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: tab === i ? 700 : 400, color: tab === i ? '#c4b5fd' : 'rgba(196,181,253,0.5)' }}>
                            {g.group}
                        </Typography>
                        {tab === i && value.length > 1 && (
                            <CloseIcon
                                sx={{ fontSize: 14, color: 'rgba(239,68,68,0.6)', ml: 0.25 }}
                                onClick={(e) => { e.stopPropagation(); removeGroup(i); }}
                            />
                        )}
                    </Box>
                ))}
                <Box onClick={addGroup} sx={{
                    px: 1, py: 0.4, borderRadius: '7px', cursor: 'pointer',
                    border: '1px dashed rgba(124,58,237,0.35)', color: 'rgba(196,181,253,0.5)',
                    '&:hover': { borderColor: 'rgba(124,58,237,0.6)', color: '#c4b5fd' },
                }}>
                    <AddIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                </Box>
            </Box>

            {activeGroup && (
                <>
                    {tab >= 0 && (
                        <TextField
                            size="small"
                            value={activeGroup.group}
                            onChange={(e) => renameGroup(tab, e.target.value)}
                            placeholder="Nazwa grupy"
                            sx={{ ...inputSx, mb: 1.5, maxWidth: 200 }}
                        />
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 1.25 }, mb: 1.5 }}>
                        {activeGroup.cards.map((c, ci) => (
                            <Box key={ci} sx={{ position: 'relative' }}>
                                <CardThumb
                                    name={c.card_name}
                                    cardData={get(c.card_name)}
                                    count={c.count}
                                    showRemoveBtn
                                    onRemove={() => removeCard(tab, ci)}
                                    onClick={() => setExpanded(expanded === ci ? null : ci)}
                                />
                            </Box>
                        ))}
                    </Box>

                    <Collapse in={expanded !== null && Boolean(activeGroup.cards[expanded])}>
                        {expanded !== null && activeGroup.cards[expanded] && (
                            <Box sx={{ p: 1.5, bgcolor: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: '8px', mb: 1.5 }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c4b5fd', mb: 1 }}>
                                    {activeGroup.cards[expanded].card_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <Label>Ilość:</Label>
                                    {[1, 2, 3, 4].map((n) => (
                                        <Box key={n} onClick={() => updateCard(tab, expanded, 'count', n)} sx={{
                                            width: 28, height: 28, borderRadius: '6px', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: activeGroup.cards[expanded].count === n ? 'rgba(124,58,237,0.4)' : 'rgba(0,0,0,0.25)',
                                            border: `1px solid ${activeGroup.cards[expanded].count === n ? 'rgba(124,58,237,0.6)' : 'rgba(76,29,149,0.2)'}`,
                                            color: activeGroup.cards[expanded].count === n ? '#c4b5fd' : 'rgba(255,255,255,0.5)',
                                            fontWeight: 700, fontSize: '0.8rem',
                                        }}>
                                            {n}
                                        </Box>
                                    ))}
                                </Box>
                                <TextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={activeGroup.cards[expanded].note || ''}
                                    onChange={(e) => updateCard(tab, expanded, 'note', e.target.value)}
                                    placeholder="Opis roli karty w talii…"
                                    sx={inputSx}
                                />
                            </Box>
                        )}
                    </Collapse>

                    <CardAutocomplete
                        options={cardOptions}
                        onSelect={(name) => addCard(tab, name)}
                        placeholder="Dodaj kartę do grupy…"
                    />
                </>
            )}
        </Box>
    );
}

// ─── Gameplan Editor ──────────────────────────────────────────────────────────

const SIDE_CYCLE = ['always', 'otp', 'otd'];
const SIDE_LABEL = { always: 'Zawsze', otp: 'OTP', otd: 'OTD' };
const SIDE_COLOR = { always: 'rgba(196,181,253,0.45)', otp: '#34d399', otd: '#60a5fa' };

function IdealForEditor({ items, onChange, archetypeOptions }) {
    const [inputValue, setInputValue] = useState('');

    const addArchetype = (opt) => {
        if (!opt) return;
        const already = items.some((x) => (x.id || x) === opt.id);
        if (!already) onChange([...items, { id: opt.id, name: opt.name, ink_colors: opt.ink_colors || [], side: 'always' }]);
        setInputValue('');
    };

    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

    const cycleSide = (idx) => {
        const current = items[idx]?.side || 'always';
        const next = SIDE_CYCLE[(SIDE_CYCLE.indexOf(current) + 1) % SIDE_CYCLE.length];
        onChange(items.map((x, i) => i === idx ? { ...x, side: next } : x));
    };

    return (
        <Box>
            {items.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                    {items.map((item, i) => {
                        const name = item.name || item;
                        const colors = item.ink_colors || [];
                        const side = item.side || 'always';
                        return (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5,
                                    px: 1, py: 0.4, borderRadius: '6px', flex: 1,
                                    bgcolor: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.3)',
                                }}>
                                    {colors.length > 0 && <InkColorIcons colors={colors} size={14} gap={0.2} tooltip={false} />}
                                    <Typography sx={{ fontSize: '0.8rem', color: '#c4b5fd', fontWeight: 500 }}>{name}</Typography>
                                </Box>
                                <Box onClick={() => cycleSide(i)} sx={{
                                    px: 0.9, py: 0.3, borderRadius: '5px', cursor: 'pointer', flexShrink: 0,
                                    bgcolor: side !== 'always' ? 'rgba(0,0,0,0.25)' : 'transparent',
                                    border: `1px solid ${side !== 'always' ? SIDE_COLOR[side] + '60' : 'rgba(76,29,149,0.25)'}`,
                                    transition: 'all 0.12s',
                                    '&:hover': { borderColor: SIDE_COLOR[side] },
                                }}>
                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: SIDE_COLOR[side], whiteSpace: 'nowrap' }}>
                                        {SIDE_LABEL[side]}
                                    </Typography>
                                </Box>
                                <IconButton size="small" onClick={() => remove(i)} sx={{ color: 'rgba(239,68,68,0.45)', p: 0.25, '&:hover': { color: '#ef4444' } }}>
                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>
            )}
            <Autocomplete
                value={null}
                inputValue={inputValue}
                onInputChange={(_, v, reason) => { if (reason !== 'reset') setInputValue(v); }}
                onChange={(_, opt) => addArchetype(opt)}
                options={archetypeOptions.filter((o) => !items.some((x) => (x.id || x) === o.id))}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                filterOptions={(opts, { inputValue: q }) => {
                    if (!q.trim()) return opts.slice(0, 30);
                    return opts.filter((o) => o.name.toLowerCase().includes(q.toLowerCase())).slice(0, 30);
                }}
                noOptionsText="Brak archetypów"
                renderOption={(props, opt) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '5px !important' }}>
                        {opt.ink_colors?.length > 0 && <InkColorIcons colors={opt.ink_colors} size={16} gap={0.25} tooltip={false} />}
                        <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.88)' }}>{opt.name}</Typography>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Dodaj archetyp…" sx={{ ...inputSx }} />
                )}
                componentsProps={{
                    popper: { sx: { '& .MuiPaper-root': { bgcolor: '#1a1033', border: '1px solid rgba(76,29,149,0.4)', borderRadius: '8px' } } },
                    paper: { sx: { bgcolor: '#1a1033' } },
                }}
            />
        </Box>
    );
}

function PlayLineEditor({ pl, onChange, onRemove, get, cardOptions, archetypeOptions }) {
    const upd = (k, v) => onChange({ ...pl, [k]: v });

    const updateTurnSeq = (side, turns) =>
        upd('turn_sequence', { ...(pl.turn_sequence || {}), [side]: turns });

    return (
        <Box sx={{ p: 1.5, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid rgba(76,29,149,0.15)', mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <TextField size="small" value={pl.title || ''} onChange={(e) => upd('title', e.target.value)}
                    placeholder="Tytuł play line…" sx={{ ...inputSx, flex: 1, mr: 1 }} />
                <IconButton size="small" onClick={onRemove} sx={{ color: 'rgba(239,68,68,0.6)' }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <Label>Punkty kluczowe</Label>
            <Box sx={{ mb: 1.5 }}>
                <StringListEditor
                    items={pl.bullet_points || []}
                    onChange={(v) => upd('bullet_points', v)}
                    placeholder="Kluczowy punkt…"
                    addLabel="Dodaj punkt"
                />
            </Box>

            <Label>Idealne na (matchupy)</Label>
            <Box sx={{ mb: 1.5 }}>
                <IdealForEditor
                    items={pl.ideal_for || []}
                    onChange={(v) => upd('ideal_for', v)}
                    archetypeOptions={archetypeOptions || []}
                />
            </Box>

            <Label>Sekwencja tur — OTP</Label>
            <Box sx={{ mb: 1.5 }}>
                <TurnListEditor
                    turns={pl.turn_sequence?.otp || []}
                    onChange={(v) => updateTurnSeq('otp', v)}
                    get={get}
                    cardOptions={cardOptions}
                />
            </Box>

            <Label>Sekwencja tur — OTD</Label>
            <TurnListEditor
                turns={pl.turn_sequence?.otd || []}
                onChange={(v) => updateTurnSeq('otd', v)}
                get={get}
                cardOptions={cardOptions}
            />
        </Box>
    );
}

function GameplanEditor({ value, onChange, get, cardOptions, archetypeOptions }) {
    const upd = (k, v) => onChange({ ...value, [k]: v });

    return (
        <Box sx={sectionSx}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c4b5fd', mb: 1.5 }}>Gameplan</Typography>

            <Label>Ogólny opis</Label>
            <TextField size="small" fullWidth multiline minRows={4} value={value.text || ''}
                onChange={(e) => upd('text', e.target.value)} placeholder="Opisz ogólny gameplan talii…"
                sx={{ ...inputSx, mb: 2 }} />

            <Label>Play lines</Label>
            {(value.play_lines || []).map((pl, i) => (
                <PlayLineEditor
                    key={i}
                    pl={pl}
                    onChange={(v) => upd('play_lines', value.play_lines.map((x, j) => (j === i ? v : x)))}
                    onRemove={() => upd('play_lines', value.play_lines.filter((_, j) => j !== i))}
                    get={get}
                    cardOptions={cardOptions}
                    archetypeOptions={archetypeOptions}
                />
            ))}
            <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => upd('play_lines', [...(value.play_lines || []), { title: '', bullet_points: [], ideal_for: [], turn_sequence: { otp: [], otd: [] } }])}
                sx={addBtnSx}>
                Dodaj play line
            </Button>
        </Box>
    );
}

// ─── Mulligan Editor ──────────────────────────────────────────────────────────

function MulliganSideEditor({ data, onChange, get, cardOptions, deckPool = [] }) {
    const upd = (k, v) => onChange({ ...data, [k]: v });

    const updateHand = (i, h) =>
        upd('example_hands', data.example_hands.map((x, j) => (j === i ? h : x)));

    const addHand = () =>
        upd('example_hands', [...(data.example_hands || []), { card_names: [], mulligan_indices: [], description: '' }]);

    const drawHand = () => {
        const hand = drawRandomHand(deckPool);
        upd('example_hands', [...(data.example_hands || []), { card_names: hand, mulligan_indices: [], description: '' }]);
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <CardNameListEditor items={data.priorities || []} onChange={(v) => upd('priorities', v)} get={get} cardOptions={cardOptions} label="Priorytetowe karty (zachowaj w ręce)" />
            </Box>

            <Label>Przykładowe ręce</Label>
            {(data.example_hands || []).map((h, i) => (
                <Box key={i} sx={{ p: 1.5, bgcolor: 'rgba(0,0,0,0.18)', borderRadius: '8px', border: '1px solid rgba(76,29,149,0.15)', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(196,181,253,0.5)' }}>Przykład {i + 1}</Typography>
                        <IconButton size="small" onClick={() => upd('example_hands', data.example_hands.filter((_, j) => j !== i))}
                            sx={{ color: 'rgba(239,68,68,0.5)' }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                    <MulliganHandBuilder
                        cardNames={h.card_names || []}
                        mulliganIndices={h.mulligan_indices || []}
                        onChange={(next) => updateHand(i, { ...h, card_names: next.card_names, mulligan_indices: next.mulligan_indices })}
                        get={get}
                        cardOptions={cardOptions}
                        deckPool={deckPool}
                    />
                    <TextField
                        size="small" fullWidth multiline minRows={2}
                        value={h.description || ''}
                        onChange={(e) => updateHand(i, { ...h, description: e.target.value })}
                        placeholder="Komentarz do tej ręki…"
                        sx={{ ...inputSx, mt: 1 }}
                    />
                </Box>
            ))}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {deckPool.length > 0 && (
                    <Button size="small" variant="outlined" startIcon={<CasinoIcon sx={{ fontSize: 14 }} />} onClick={drawHand}
                        sx={{ ...addBtnSx, borderColor: 'rgba(124,58,237,0.45)', color: '#c4b5fd',
                            '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(124,58,237,0.12)', color: 'white' } }}>
                        Losuj rękę
                    </Button>
                )}
                <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={addHand} sx={addBtnSx}>
                    Dodaj pustą rękę
                </Button>
            </Box>
        </Box>
    );
}

function MulliganEditor({ value, onChange, get, cardOptions, deckPool = [] }) {
    const [side, setSide] = useState(0);
    const upd = (k, v) => onChange({ ...value, [k]: v });
    const isUnified = value.unified !== false;

    const toggleMode = () => {
        if (isUnified) {
            upd('unified', false);
        } else {
            upd('unified', true);
        }
    };

    return (
        <Box sx={sectionSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c4b5fd' }}>Mulligan</Typography>
                <Box onClick={toggleMode} sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, cursor: 'pointer',
                    borderRadius: '7px', border: '1px solid rgba(76,29,149,0.3)',
                    bgcolor: isUnified ? 'transparent' : 'rgba(124,58,237,0.18)',
                    color: isUnified ? 'rgba(196,181,253,0.45)' : '#c4b5fd',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: 'rgba(124,58,237,0.12)', color: '#c4b5fd', borderColor: 'rgba(124,58,237,0.5)' },
                }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: isUnified ? 400 : 600 }}>
                        {isUnified ? 'Jeden mulligan' : 'Różny OTP / OTD'}
                    </Typography>
                    <Box sx={{
                        width: 28, height: 16, borderRadius: '8px', bgcolor: isUnified ? 'rgba(76,29,149,0.3)' : '#7c3aed',
                        position: 'relative', transition: 'all 0.2s', flexShrink: 0,
                    }}>
                        <Box sx={{
                            position: 'absolute', top: 2, left: isUnified ? 2 : 14, width: 12, height: 12,
                            borderRadius: '50%', bgcolor: 'white', transition: 'all 0.2s',
                        }} />
                    </Box>
                </Box>
            </Box>

            <Label>Uwagi ogólne</Label>
            <TextField size="small" fullWidth multiline minRows={2} value={value.general_notes || ''}
                onChange={(e) => upd('general_notes', e.target.value)} placeholder="Ogólne zasady mulliganu…"
                sx={{ ...inputSx, mb: 2 }} />

            {!isUnified && (
                <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                    {[{ key: 'otp', label: 'On the Play' }, { key: 'otd', label: 'On the Draw' }].map(({ key, label }, i) => (
                        <Box key={key} onClick={() => setSide(i)} sx={{
                            px: 1.5, py: 0.4, borderRadius: '6px', cursor: 'pointer',
                            bgcolor: side === i ? 'rgba(124,58,237,0.25)' : 'transparent',
                            border: `1px solid ${side === i ? 'rgba(124,58,237,0.5)' : 'rgba(76,29,149,0.2)'}`,
                            transition: 'all 0.15s',
                        }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: side === i ? 700 : 400, color: side === i ? '#c4b5fd' : 'rgba(196,181,253,0.4)' }}>
                                {label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {isUnified ? (
                <MulliganSideEditor data={value.otp || {}} onChange={(v) => upd('otp', v)} get={get} cardOptions={cardOptions} deckPool={deckPool} />
            ) : side === 0 ? (
                <MulliganSideEditor data={value.otp || {}} onChange={(v) => upd('otp', v)} get={get} cardOptions={cardOptions} deckPool={deckPool} />
            ) : (
                <MulliganSideEditor data={value.otd || {}} onChange={(v) => upd('otd', v)} get={get} cardOptions={cardOptions} deckPool={deckPool} />
            )}
        </Box>
    );
}

// ─── Matchups Editor ──────────────────────────────────────────────────────────

function CardNameListEditor({ items = [], onChange, get, cardOptions, label }) {
    const addCard = (name) => {
        if (!items.find((c) => (c.card_name || c) === name)) {
            onChange([...items, { card_name: name }]);
        }
    };
    return (
        <Box>
            <Label>{label}</Label>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
                {items.map((c, i) => {
                    const name = c.card_name || c;
                    const cd = get(name);
                    return (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.25,
                            bgcolor: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)',
                            borderRadius: '6px', px: 0.75, py: 0.2 }}>
                            {cd?.thumbnail && (
                                <Box component="img" src={cd.thumbnail} alt={name}
                                    sx={{ height: 22, width: 16, borderRadius: '2px', objectFit: 'cover' }} />
                            )}
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)' }}>{name}</Typography>
                            <IconButton size="small" onClick={() => onChange(items.filter((_, j) => j !== i))}
                                sx={{ p: 0, color: 'rgba(239,68,68,0.5)', ml: 0.25 }}>
                                <CloseIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                        </Box>
                    );
                })}
            </Box>
            <CardAutocomplete options={cardOptions} onSelect={addCard} placeholder={`Dodaj kartę…`} />
        </Box>
    );
}

function ReplayLinksEditor({ items = [], onChange }) {
    const upd = (i, field, val) =>
        onChange(items.map((item, j) => (j === i ? { ...item, [field]: val } : item)));
    const add = () => onChange([...items, { url: '', label: '' }]);
    const remove = (i) => onChange(items.filter((_, j) => j !== i));

    return (
        <Box>
            <Label>Powtórki / przykładowe gry</Label>
            {items.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.75, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        value={item.url || ''}
                        onChange={(e) => upd(i, 'url', e.target.value)}
                        placeholder="https://duels.ink/replay/…"
                        sx={{ ...inputSx, flex: 2 }}
                    />
                    <TextField
                        size="small"
                        value={item.label || ''}
                        onChange={(e) => upd(i, 'label', e.target.value)}
                        placeholder="Opis (opcjonalnie)"
                        sx={{ ...inputSx, flex: 1 }}
                    />
                    <IconButton size="small" onClick={() => remove(i)}
                        sx={{ color: 'rgba(239,68,68,0.5)' }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>
            ))}
            <Button size="small" variant="outlined" startIcon={<PlayCircleOutlineIcon sx={{ fontSize: 14 }} />} onClick={add} sx={addBtnSx}>
                Dodaj powtórkę
            </Button>
        </Box>
    );
}

function ArchetypeAutocomplete({ value, onChange, archetypeOptions }) {
    const selectedOption = archetypeOptions.find((o) => o.id === value) || null;

    return (
        <Autocomplete
            value={selectedOption}
            onChange={(_, opt) => {
                if (opt) {
                    onChange({ id: opt.id, name: opt.name, ink_colors: opt.ink_colors || [] });
                } else {
                    onChange(null);
                }
            }}
            options={archetypeOptions}
            getOptionLabel={(o) => o.name || o.id || ''}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            filterOptions={(opts, { inputValue }) => {
                if (!inputValue.trim()) return opts.slice(0, 30);
                const q = inputValue.toLowerCase();
                return opts.filter((o) => o.name.toLowerCase().includes(q)).slice(0, 30);
            }}
            noOptionsText="Brak archetypów"
            renderOption={(props, opt) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '5px !important' }}>
                    {opt.ink_colors?.length > 0 && (
                        <InkColorIcons colors={opt.ink_colors} size={16} gap={0.25} tooltip={false} />
                    )}
                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.88)' }}>{opt.name}</Typography>
                </Box>
            )}
            renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Wybierz archetyp…" sx={{ ...inputSx, minWidth: 220 }} />
            )}
            componentsProps={{
                popper: { sx: { '& .MuiPaper-root': { bgcolor: '#1a1033', border: '1px solid rgba(76,29,149,0.4)', borderRadius: '8px' } } },
                paper: { sx: { bgcolor: '#1a1033' } },
            }}
        />
    );
}

function MatchupEditor({ m, onChange, onRemove, get, cardOptions, ownCardOptions, archetypeOptions, deckPool = [] }) {
    const upd = (k, v) => onChange({ ...m, [k]: v });
    const [open, setOpen] = useState(false);

    const matchupCardOptions = useMemo(
        () => filterCardsByInkColors(cardOptions, m.ink_colors),
        [cardOptions, m.ink_colors]
    );

    const updateMulliganSide = (side, v) =>
        upd(side === 'otp' ? 'mulligan_otp' : 'mulligan_otd', v);

    const handleArchetypeSelect = (opt) => {
        if (opt) {
            onChange({ ...m, archetype_id: opt.id, archetype_name: opt.name, ink_colors: opt.ink_colors || [] });
        }
    };

    return (
        <Accordion disableGutters expanded={open} onChange={(_, v) => setOpen(v)} sx={{
            bgcolor: 'rgba(17,24,39,0.5)', border: '1px solid rgba(76,29,149,0.2)',
            borderRadius: '10px !important', mb: 1, overflow: 'hidden',
            '&:before': { display: 'none' },
        }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(196,181,253,0.5)' }} />}
                sx={{ px: 1.5, minHeight: 48 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    {m.ink_colors?.length > 0 && (
                        <InkColorIcons colors={m.ink_colors} size={16} gap={0.25} tooltip={false} />
                    )}
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: m.archetype_name ? 'white' : 'rgba(196,181,253,0.45)' }}>
                        {m.archetype_name || 'Nowy matchup'}
                    </Typography>
                    {m.favorability && (
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(196,181,253,0.5)' }}>({m.favorability})</Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }}
                        sx={{ color: 'rgba(239,68,68,0.5)', mr: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: '1px solid rgba(76,29,149,0.15)' }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <ArchetypeAutocomplete
                        value={m.archetype_id || ''}
                        onChange={handleArchetypeSelect}
                        archetypeOptions={archetypeOptions}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select value={m.favorability || 'even'} onChange={(e) => upd('favorability', e.target.value)} sx={selectSx}>
                            {FAVORABILITY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>

                <Label>Gameplan w tym matchupie</Label>
                <TextField size="small" fullWidth multiline minRows={3} value={m.gameplan || ''}
                    onChange={(e) => upd('gameplan', e.target.value)} placeholder="Jak gramy ten matchup…"
                    sx={{ ...inputSx, mb: 1.5 }} />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <CardNameListEditor items={m.key_cards || []} onChange={(v) => upd('key_cards', v)} get={get} cardOptions={ownCardOptions || cardOptions} label="Kluczowe karty" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <CardNameListEditor items={m.threats || []} onChange={(v) => upd('threats', v)} get={get} cardOptions={matchupCardOptions} label="Zagrożenia" />
                    </Box>
                </Box>

                <Label>Tipy</Label>
                <Box sx={{ mb: 1.5 }}>
                    <StringListEditor items={m.tips || []} onChange={(v) => upd('tips', v)} placeholder="Tip…" addLabel="Dodaj tip" />
                </Box>

                <Box sx={{ mb: 1.5 }}>
                    <ReplayLinksEditor items={m.replays || []} onChange={(v) => upd('replays', v)} />
                </Box>

                <Accordion disableGutters sx={{ bgcolor: 'rgba(0,0,0,0.15)', border: '1px solid rgba(76,29,149,0.12)', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(196,181,253,0.4)', fontSize: 18 }} />} sx={{ px: 1.25, minHeight: 36 }}>
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(196,181,253,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Mulligan (opcjonalnie)
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1.25, pb: 1.25, pt: 0.5 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(196,181,253,0.6)', mb: 0.75 }}>OTP</Typography>
                        <MulliganSideEditor
                            data={m.mulligan_otp || { priorities: [], ideal_line: [], example_hands: [] }}
                            onChange={(v) => updateMulliganSide('otp', v)}
                            get={get} cardOptions={cardOptions} deckPool={deckPool}
                        />
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(196,181,253,0.6)', mt: 1.5, mb: 0.75 }}>OTD</Typography>
                        <MulliganSideEditor
                            data={m.mulligan_otd || { priorities: [], ideal_line: [], example_hands: [] }}
                            onChange={(v) => updateMulliganSide('otd', v)}
                            get={get} cardOptions={cardOptions} deckPool={deckPool}
                        />
                    </AccordionDetails>
                </Accordion>
            </AccordionDetails>
        </Accordion>
    );
}

function MatchupsEditor({ value = [], onChange, get, cardOptions, ownCardOptions, archetypeOptions, deckPool = [] }) {
    const addMatchup = () =>
        onChange([...value, { archetype_id: '', archetype_name: '', ink_colors: [], favorability: 'even', gameplan: '', key_cards: [], threats: [], tips: [], replays: [] }]);

    return (
        <Box sx={sectionSx}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c4b5fd', mb: 1.5 }}>Matchupy</Typography>
            {value.map((m, i) => (
                <MatchupEditor
                    key={i}
                    m={m}
                    onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))}
                    onRemove={() => onChange(value.filter((_, j) => j !== i))}
                    get={get}
                    cardOptions={cardOptions}
                    ownCardOptions={ownCardOptions}
                    archetypeOptions={archetypeOptions}
                    deckPool={deckPool}
                />
            ))}
            <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={addMatchup} sx={addBtnSx}>
                Dodaj matchup
            </Button>
        </Box>
    );
}

// ─── Alternative Builds Editor ────────────────────────────────────────────────

function AltBuildItem({ build, onUpdate, onDelete, deckOptions }) {
    const [inputVal, setInputVal] = useState('');
    const currentDeck = deckOptions.find(d => d.id === build.deck_id) || null;

    return (
        <Box sx={{ p: 1.5, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid rgba(76,29,149,0.15)', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Autocomplete
                    value={currentDeck}
                    inputValue={inputVal}
                    onInputChange={(_, v, reason) => { if (reason !== 'reset') setInputVal(v); }}
                    onChange={(_, opt) => { onUpdate('deck_id', opt?.id || null); setInputVal(''); }}
                    options={deckOptions}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    noOptionsText={inputVal.trim() ? 'Brak wyników' : 'Wpisz nazwę talii…'}
                    filterOptions={(opts, { inputValue }) => {
                        if (!inputValue.trim()) return opts.slice(0, 50);
                        const q = inputValue.toLowerCase();
                        return opts.filter(o => o.name.toLowerCase().includes(q)).slice(0, 50);
                    }}
                    renderOption={(props, opt) => (
                        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '6px !important' }}>
                            {opt.ink_colors?.length > 0 && <InkColorIcons colors={opt.ink_colors} size={14} gap={0.2} tooltip={false} sx={{ flexShrink: 0 }} />}
                            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.88)' }}>{opt.name}</Typography>
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField {...params} size="small" placeholder="Wybierz talię wariantu…" sx={inputSx} />
                    )}
                    componentsProps={{
                        popper: { sx: { '& .MuiPaper-root': { bgcolor: '#1a1033', border: '1px solid rgba(76,29,149,0.4)', borderRadius: '8px' } } },
                        paper: { sx: { bgcolor: '#1a1033' } },
                    }}
                    sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={onDelete} sx={{ color: 'rgba(239,68,68,0.6)', ml: 1, flexShrink: 0 }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
            {currentDeck && (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {currentDeck.ink_colors?.length > 0 && <InkColorIcons colors={currentDeck.ink_colors} size={13} gap={0.2} tooltip={false} />}
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(196,181,253,0.7)' }}>{currentDeck.name}</Typography>
                </Box>
            )}
            <TextField size="small" fullWidth value={build.when_to_consider || ''} onChange={(e) => onUpdate('when_to_consider', e.target.value)}
                placeholder="Kiedy rozważyć…" label="Kiedy rozważyć" sx={{ ...inputSx, mb: 1 }} />
            <TextField size="small" fullWidth multiline minRows={2} value={build.description || ''} onChange={(e) => onUpdate('description', e.target.value)}
                placeholder="Opis wariantu…" sx={inputSx} />
        </Box>
    );
}

function AltBuildsEditor({ value = [], onChange, internalDecks = [] }) {
    return (
        <Box sx={sectionSx}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c4b5fd', mb: 1.5 }}>Alternatywne buildy</Typography>
            {value.map((b, i) => (
                <AltBuildItem
                    key={i}
                    build={b}
                    onUpdate={(k, v) => onChange(value.map((b2, j) => (j === i ? { ...b2, [k]: v } : b2)))}
                    onDelete={() => onChange(value.filter((_, j) => j !== i))}
                    deckOptions={internalDecks}
                />
            ))}
            <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => onChange([...value, { deck_id: null, when_to_consider: '', description: '' }])} sx={addBtnSx}>
                Dodaj wariant
            </Button>
        </Box>
    );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

const filterCardsByInkColors = (options, inkColors) => {
    if (!inkColors || inkColors.length === 0) return options;
    const allowed = new Set(inkColors.map((c) => c.toLowerCase()));
    return options.filter((o) => {
        const cardColors = o.magic_ink_colors || [];
        return cardColors.length === 0 || cardColors.some((c) => allowed.has(c.toLowerCase()));
    });
};

const EDITOR_TABS = [
    { id: 'basics',   label: 'Podstawy',           desc: 'Talia, podsumowanie, tagi' },
    { id: 'cards',    label: 'Karty',               desc: 'Core / Flex / Tech' },
    { id: 'gameplan', label: 'Gameplan & Mulligan', desc: 'Strategia i otwieranie ręki' },
    { id: 'matchups', label: 'Matchupy',            desc: 'Matchupy vs inne archetypy' },
    { id: 'builds',   label: 'Warianty',            desc: 'Alternatywne buildy' },
];

export default function GuideEditor({ initialContent, onSave, onCancel, saving, saveError, trackerFilters, archetypeId, archetypeInkColors }) {
    const [mode, setMode] = useState('visual');
    const [editorTab, setEditorTab] = useState('basics');
    const [draft, setDraft] = useState(() => blankDraft(initialContent));
    const [jsonText, setJsonText] = useState('');
    const [jsonError, setJsonError] = useState(null);

    const { options: cardOptions, get, ready: cardsReady } = useCardOptions();
    const archetypeOptions = useArchetypeOptions(trackerFilters);
    const internalDecks = useInternalDecks(trackerFilters, archetypeId);
    const deckCardKeys = useDeckCards(draft.meta?.deck_list_id);
    const deckPool = useDeckPool(draft.meta?.deck_list_id);

    const inkFilteredCardOptions = useMemo(
        () => filterCardsByInkColors(cardOptions, archetypeInkColors),
        [cardOptions, archetypeInkColors]
    );

    const scopedCardOptions = useMemo(() => {
        const breakdownKeys = new Set();
        (draft.card_breakdown || []).forEach((group) => {
            (group.cards || []).forEach((c) => {
                if (c.card_name) breakdownKeys.add(c.card_name.toLowerCase().trim());
            });
        });
        const allowed = new Set([...deckCardKeys, ...breakdownKeys]);
        if (allowed.size === 0) return inkFilteredCardOptions;
        return inkFilteredCardOptions.filter((opt) => allowed.has(opt.label.toLowerCase().trim()));
    }, [inkFilteredCardOptions, deckCardKeys, draft.card_breakdown]);

    const switchToJson = () => {
        setJsonText(JSON.stringify(draft, null, 2));
        setJsonError(null);
        setMode('json');
    };

    const switchToVisual = () => {
        try {
            const parsed = JSON.parse(jsonText);
            setDraft(blankDraft(parsed));
            setJsonError(null);
            setMode('visual');
        } catch {
            setJsonError('Nieprawidłowy JSON — popraw błędy składni przed powrotem do edytora.');
        }
    };

    const handleSave = () => {
        if (mode === 'json') {
            try {
                onSave(JSON.parse(jsonText));
            } catch {
                setJsonError('Nieprawidłowy JSON — nie można zapisać.');
            }
        } else {
            onSave(draft);
        }
    };

    const upd = (key, val) => setDraft((prev) => ({ ...prev, [key]: val }));

    const tabIndex = EDITOR_TABS.findIndex((t) => t.id === editorTab);
    const prevTab = tabIndex > 0 ? EDITOR_TABS[tabIndex - 1] : null;
    const nextTab = tabIndex < EDITOR_TABS.length - 1 ? EDITOR_TABS[tabIndex + 1] : null;

    const navBtnSx = {
        textTransform: 'none', fontSize: '0.78rem', fontWeight: 500,
        borderColor: 'rgba(76,29,149,0.4)', color: 'rgba(196,181,253,0.7)',
        '&:hover': { borderColor: 'rgba(124,58,237,0.6)', bgcolor: 'rgba(124,58,237,0.06)', color: '#c4b5fd' },
    };

    return (
        <Box>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{
                    display: 'flex', gap: '2px', p: '3px',
                    bgcolor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(76,29,149,0.3)',
                    borderRadius: '8px',
                }}>
                    {[
                        { id: 'visual', icon: <GridViewIcon sx={{ fontSize: 16 }} />, label: 'Wizualnie' },
                        { id: 'json', icon: <CodeIcon sx={{ fontSize: 16 }} />, label: 'JSON' },
                    ].map(({ id, icon, label }) => {
                        const active = mode === id;
                        return (
                            <Box key={id} onClick={id === 'json' ? switchToJson : switchToVisual} sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5,
                                borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s',
                                bgcolor: active ? 'rgba(124,58,237,0.35)' : 'transparent',
                                border: `1px solid ${active ? 'rgba(124,58,237,0.5)' : 'transparent'}`,
                                color: active ? '#c4b5fd' : 'rgba(196,181,253,0.5)',
                                '&:hover': { bgcolor: active ? undefined : 'rgba(124,58,237,0.1)', color: active ? undefined : 'rgba(196,181,253,0.8)' },
                            }}>
                                {icon}
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: active ? 700 : 400 }}>{label}</Typography>
                            </Box>
                        );
                    })}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" startIcon={<CloseIcon sx={{ fontSize: 15 }} />} onClick={onCancel} disabled={saving}
                        sx={{ color: 'rgba(196,181,253,0.7)', borderColor: 'rgba(76,29,149,0.4)', textTransform: 'none', fontSize: '0.78rem', '&:hover': { borderColor: 'rgba(124,58,237,0.6)', bgcolor: 'rgba(124,58,237,0.06)' } }}>
                        Anuluj
                    </Button>
                    <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: 15 }} />}
                        sx={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', textTransform: 'none', fontSize: '0.78rem', '&:hover': { background: 'linear-gradient(135deg,#6d28d9,#5b21b6)' } }}>
                        {saving ? 'Zapisuję…' : 'Zapisz'}
                    </Button>
                </Box>
            </Box>

            {saveError && <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.82rem' }}>{saveError}</Alert>}
            {jsonError && <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.82rem' }}>{jsonError}</Alert>}

            {/* JSON mode */}
            {mode === 'json' && (
                <TextField
                    multiline fullWidth minRows={16} maxRows={40}
                    value={jsonText}
                    onChange={(e) => setJsonText(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            color: 'rgba(255,255,255,0.9)', fontFamily: 'monospace', fontSize: '0.8rem',
                            bgcolor: 'rgba(0,0,0,0.25)',
                            '& fieldset': { borderColor: 'rgba(76,29,149,0.4)' },
                            '&:hover fieldset': { borderColor: 'rgba(124,58,237,0.5)' },
                            '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
                        },
                    }}
                />
            )}

            {/* Visual mode */}
            {mode === 'visual' && (
                <Box>
                    {!cardsReady && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1 }}>
                            <CircularProgress size={16} sx={{ color: '#7c3aed' }} />
                            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(196,181,253,0.5)' }}>Ładowanie kart…</Typography>
                        </Box>
                    )}

                    {/* Section tab bar */}
                    <Box sx={{
                        display: 'flex', gap: '3px', mb: 2, flexWrap: 'wrap',
                        p: '4px',
                        bgcolor: 'rgba(0,0,0,0.25)',
                        border: '1px solid rgba(76,29,149,0.2)',
                        borderRadius: '10px',
                    }}>
                        {EDITOR_TABS.map(({ id, label }, idx) => {
                            const active = editorTab === id;
                            return (
                                <Tooltip key={id} title={EDITOR_TABS[idx].desc} placement="bottom" arrow>
                                    <Box onClick={() => setEditorTab(id)} sx={{
                                        px: { xs: 1.25, sm: 1.75 }, py: 0.75,
                                        borderRadius: '7px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        bgcolor: active ? 'rgba(124,58,237,0.38)' : 'transparent',
                                        border: `1px solid ${active ? 'rgba(124,58,237,0.55)' : 'transparent'}`,
                                        color: active ? '#c4b5fd' : 'rgba(196,181,253,0.45)',
                                        '&:hover': {
                                            bgcolor: active ? undefined : 'rgba(124,58,237,0.1)',
                                            color: active ? undefined : 'rgba(196,181,253,0.72)',
                                        },
                                    }}>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap' }}>
                                            {label}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>

                    {/* Tab content */}
                    {editorTab === 'basics' && (
                        <>
                            <MetaDeckEditor
                                deckListId={draft.meta?.deck_list_id}
                                onChange={(id) => upd('meta', { ...draft.meta, deck_list_id: id })}
                                deckOptions={internalDecks}
                            />
                            <TldrEditor value={draft.tldr} onChange={(v) => upd('tldr', v)} get={get} cardOptions={scopedCardOptions} />
                        </>
                    )}
                    {editorTab === 'cards' && (
                        <CardBreakdownEditor value={draft.card_breakdown} onChange={(v) => upd('card_breakdown', v)} get={get} cardOptions={cardOptions} />
                    )}
                    {editorTab === 'gameplan' && (
                        <>
                            <GameplanEditor value={draft.gameplan} onChange={(v) => upd('gameplan', v)} get={get} cardOptions={scopedCardOptions} archetypeOptions={archetypeOptions} />
                            <MulliganEditor value={draft.mulligan} onChange={(v) => upd('mulligan', v)} get={get} cardOptions={scopedCardOptions} deckPool={deckPool} />
                        </>
                    )}
                    {editorTab === 'matchups' && (
                        <MatchupsEditor value={draft.matchups} onChange={(v) => upd('matchups', v)} get={get} cardOptions={cardOptions} ownCardOptions={inkFilteredCardOptions} archetypeOptions={archetypeOptions} deckPool={deckPool} />
                    )}
                    {editorTab === 'builds' && (
                        <AltBuildsEditor value={draft.alternative_builds} onChange={(v) => upd('alternative_builds', v)} internalDecks={internalDecks} />
                    )}

                    {/* Prev / Next navigation */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 1.5, borderTop: '1px solid rgba(76,29,149,0.15)' }}>
                        <Box>
                            {prevTab && (
                                <Button size="small" variant="outlined" onClick={() => setEditorTab(prevTab.id)} sx={navBtnSx}>
                                    ← {prevTab.label}
                                </Button>
                            )}
                        </Box>
                        <Box>
                            {nextTab && (
                                <Button size="small" variant="outlined" onClick={() => setEditorTab(nextTab.id)} sx={navBtnSx}>
                                    {nextTab.label} →
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
